import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Building2, UserCircle2, Users } from 'lucide-react';
import { ADMIN_PALETTE } from '../adminDesignSystem';
import { API, formatEur, StatCard, StatusPill } from './complianceShared';

export function DAC7Tab({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/compliance/dac7`, { headers });
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load DAC7');
      } finally {
        setLoading(false);
      }
    })();
  }, [headers]);

  if (loading) return <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>;
  if (!data) return null;

  const renderRow = (row) => (
    <li
      key={`${row.kind}-${row.id}`}
      data-testid={`dac7-row-${row.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 100px 130px 140px',
        gap: 10,
        padding: 10,
        borderRadius: 10,
        background: ADMIN_PALETTE.surface,
        border: `1px solid ${ADMIN_PALETTE.border}`,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {row.kind === 'venue' ? (
          <Building2 size={14} color={ADMIN_PALETTE.inkMuted} />
        ) : (
          <UserCircle2 size={14} color={ADMIN_PALETTE.inkMuted} />
        )}
        <span style={{ fontSize: 13, color: ADMIN_PALETTE.ink }}>{row.name}</span>
        {!row.vat_number && (
          <span
            style={{
              fontSize: 10,
              padding: '2px 6px',
              borderRadius: 999,
              background: '#FEE2E2',
              color: '#991B1B',
              fontWeight: 700,
            }}
          >
            NO VAT
          </span>
        )}
      </div>
      <span style={{ fontSize: 13, color: ADMIN_PALETTE.ink, textAlign: 'right' }}>{row.txn_count} txns</span>
      <span style={{ fontSize: 13, color: ADMIN_PALETTE.ink, textAlign: 'right' }}>
        {formatEur(row.consideration_eur)}
      </span>
      <StatusPill status={row.status} />
    </li>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: ADMIN_PALETTE.inkMuted }}>
        Fiscal year <strong>{data.fiscal_year}</strong> · Threshold: 30 transactions OR €2,000 consideration in a
        calendar year triggers DAC7 reporting to A.A.D.E.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <StatCard icon={Users} label="Venues tracked" value={data.totals.venues_total} tone="info" testid="dac7-stat-venues" />
        <StatCard icon={UserCircle2} label="Hosts tracked" value={data.totals.hosts_total} tone="info" testid="dac7-stat-hosts" />
        <StatCard icon={AlertTriangle} label="Reportable" value={data.totals.reportable_count} tone="danger" testid="dac7-stat-reportable" />
        <StatCard icon={AlertTriangle} label="Approaching" value={data.totals.approaching_count} tone="warn" testid="dac7-stat-approaching" />
      </div>

      <h3 style={{ margin: '10px 0 6px', fontFamily: "'Inria Serif', serif", fontSize: 17, color: ADMIN_PALETTE.ink }}>
        Venues
      </h3>
      {data.venues.length === 0 ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>No venue activity in {data.fiscal_year}.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.venues.map(renderRow)}
        </ul>
      )}

      <h3 style={{ margin: '14px 0 6px', fontFamily: "'Inria Serif', serif", fontSize: 17, color: ADMIN_PALETTE.ink }}>
        Hosts
      </h3>
      {data.hosts.length === 0 ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>No host activity in {data.fiscal_year}.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.hosts.map(renderRow)}
        </ul>
      )}
    </div>
  );
}
